import { Box } from '@mui/material';
import { SeverityLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import colors from '../../../../assets/theme/base/colors';
import { formatRelativeGap } from '../Shared/deviationsCommon';

interface HeatMapCellProps {
    gap: number;
    gapCount: number;
    gapPctRelative?: number | null;
    target: number;
    targetCount: number;
    current: number;
    currentCount: number;
    severity: SeverityLevel;
    onClick?: () => void;
    showHeadcounts?: boolean;
    dimmed?: boolean;
    hidePercentages?: boolean;
    hideTarget?: boolean;
    cellWidth?: number;
    priorityRank?: string | null;
}

const severityStyles: Record<SeverityLevel, { bg: string; bgHover: string; iconColor: string }> = {
    [SeverityLevel.Critical]:        { bg: colors.deviationSeverity.critical.bg,         bgHover: colors.deviationSeverity.critical.bgHover,         iconColor: colors.deviationSeverity.critical.icon },
    [SeverityLevel.High]:            { bg: colors.deviationSeverity.high.bg,             bgHover: colors.deviationSeverity.high.bgHover,             iconColor: colors.deviationSeverity.high.icon },
    [SeverityLevel.Medium]:          { bg: colors.deviationSeverity.medium.bg,           bgHover: colors.deviationSeverity.medium.bgHover,           iconColor: colors.deviationSeverity.medium.icon },
    [SeverityLevel.OnTrack]:         { bg: colors.deviationSeverity.onTrack.bg,          bgHover: colors.deviationSeverity.onTrack.bgHover,          iconColor: colors.deviationSeverity.onTrack.icon },
    [SeverityLevel.OverRepresented]: { bg: colors.deviationSeverity.overRepresented.bg,  bgHover: colors.deviationSeverity.overRepresented.bgHover,  iconColor: colors.deviationSeverity.overRepresented.icon },
};

export function HeatMapCell({
    gap,
    gapCount,
    gapPctRelative,
    target,
    targetCount,
    current,
    currentCount,
    severity,
    onClick,
    showHeadcounts = true,
    dimmed = false,
    hidePercentages = false,
    hideTarget = false,
    cellWidth = 130,
    priorityRank,
}: HeatMapCellProps) {
    const styles = severityStyles[severity];
    const gapSign = gapCount > 0 ? '+' : '';

    const rankLabel = priorityRank ?? '';

    const tooltipLines = [
        rankLabel ? `Priority: ${rankLabel}` : null,
        `Relative % Gap: ${formatRelativeGap(gapPctRelative)}`,
        `Variance: ${gapSign}${gapCount} people (${gapSign}${gap}%)`,
        `Current: ${currentCount} people (${current}%)`,
        `Target: ${targetCount} people (${target}%)`,
    ].filter(Boolean).join('\n');

    return (
        <Box
            component="button"
            onClick={onClick}
            title={tooltipLines}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: cellWidth,
                height: showHeadcounts ? 84 : 56,
                borderRadius: '4px',
                overflow: 'hidden',
                cursor: onClick ? 'pointer' : 'default',
                opacity: dimmed ? 0.15 : 1,
                filter: dimmed ? 'grayscale(100%)' : 'none',
                transition: 'all 0.15s ease',
                backgroundColor: styles.bg,
                border: 'none',
                outline: 'none',
                flexShrink: 0,
                p: 0,
                '&:hover': dimmed ? {} : {
                    transform: 'scale(1.05)',
                    boxShadow: colors.ui.cellHoverShadow,
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                    height: '28px',
                    px: '8px',
                    backgroundColor: styles.bgHover,
                }}
            >
                <Box component="span" sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1, color: styles.iconColor }}>
                    {rankLabel}
                </Box>
                <Box component="span" sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1, color: styles.iconColor }}>
                    {formatRelativeGap(gapPctRelative)}
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Box component="span" sx={{ fontSize: '16px', fontWeight: 700, lineHeight: 1, color: styles.iconColor }}>
                        {gapSign}{gapCount}
                    </Box>
                    {!hidePercentages && (
                        <Box component="span" sx={{ fontSize: '10px', lineHeight: 1, color: styles.iconColor }}>
                            ({gapSign}{gap}%)
                        </Box>
                    )}
                </Box>

                {!hideTarget && showHeadcounts && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', color: colors.dark.main }}>
                        <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1 }}>
                            {currentCount}
                        </Box>
                        {!hidePercentages && (
                            <Box component="span" sx={{ fontSize: '10px', color: colors.ui.secondaryText, lineHeight: 1 }}>
                                ({current}%)
                            </Box>
                        )}
                        <Box component="span" sx={{ fontSize: '0.875rem', color: colors.ui.tertiaryText, lineHeight: 1, mx: '2px' }}>
                            |
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1 }}>
                            {targetCount}
                        </Box>
                        {!hidePercentages && (
                            <Box component="span" sx={{ fontSize: '10px', color: colors.ui.secondaryText, lineHeight: 1 }}>
                                ({target}%)
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
